import type { Content } from '~api';
import { api } from '~portal/api';

/**
 * The company directory query (`content.operatorGroups`), in the two shapes its readers need, and
 * the brand directory (`content.operators`). One
 * module so tests replace the directory in one place: `test/setup.ts` serves a directory equal to
 * the fixture by default, and a test that cares overrides it.
 */

/** @desc How long a directory read is reused; the backend refreshes it every five minutes too. */
export const DIRECTORY_STALE_MS = 5 * 60 * 1_000;

/** @desc The directory, suspending until it has loaded; a failure reaches the error boundary. */
export const useOperatorDirectory = (): readonly Content.OperatorGroup[] =>
  api.content.operatorGroups.useSuspenseQuery(undefined, { staleTime: DIRECTORY_STALE_MS })[0];

/**
 * @desc Start the brand read without suspending, so a page that then suspends on the company
 * directory fetches both at once rather than one after the other.
 */
export const usePrefetchOperatorBrands = (): void =>
  api.content.operators.usePrefetchQuery(undefined, { staleTime: DIRECTORY_STALE_MS });

/** @desc The brands (`content.operators`), suspending until loaded; a company's brands come from it. */
export const useOperatorBrands = (): readonly Content.Operator[] =>
  api.content.operators.useSuspenseQuery(undefined, { staleTime: DIRECTORY_STALE_MS })[0];

/** @desc The non-suspending read, requested only while `enabled` (Home's tracked pages). */
export const useOperatorDirectoryQuery = (enabled: boolean) => {
  const query = api.content.operatorGroups.useQuery(undefined, {
    enabled,
    staleTime: DIRECTORY_STALE_MS,
  });
  return {
    data: query.data as readonly Content.OperatorGroup[] | undefined,
    isPending: query.isPending,
    error: query.error ? { message: query.error.message } : null,
  };
};
